import { createApi } from '@reduxjs/toolkit/query/react'

import { staggeredBaseQueryWithBailOut } from '../libs/services'
import { unversionedApiUrl } from './common'
import { ServiceBaseUrl } from './serviceApi'
import { ServicesApiTag, ServicesReducerPath } from './constants'
import qs from 'qs'
import { getSystemHeaders } from '../libs/system'
import {
	ContactLensesOptions,
	ContactLensesOptionsParams,
	ContactLensesOptionWithName,
	UpriseLensTypeId,
} from '../model/contactLenses'
import { ContactLensesBase, ContactLensesCatalogData } from '../model/exam'
import { useDebounce } from '../hooks/useDebounce'

export const contactLensesApi = createApi({
	reducerPath: ServicesReducerPath.ContactLensesApi,
	tagTypes: [ServicesApiTag.ContactLenses],
	baseQuery: staggeredBaseQueryWithBailOut(unversionedApiUrl),
	endpoints: builder => ({
		get: builder.query<
			ContactLensesCatalogData[],
			{ query?: string; legalEntityId?: string, limit?: number }
		>({
			query: ({ query, legalEntityId, limit }) => {
				const params: Record<string, string | number> = {}
				if (query) params.q = query
				if (legalEntityId) params.legalEntityId = legalEntityId
				if (limit) params.limit = limit
				const queryString = Object.keys(params).length
					? `?${qs.stringify(params)}`
					: ''
				return {
					url: `${ServiceBaseUrl.ContactLenses}${queryString}`,
					headers: getSystemHeaders(),
				}
			},
			providesTags: [ServicesApiTag.ContactLenses],
			keepUnusedDataFor: 0,
		}),

		getOptions: builder.query<ContactLensesOptions, ContactLensesOptionsParams>(
			{
				query: query => ({
					url: `${ServiceBaseUrl.ContactLenses}/options?${qs.stringify(query)}`,
					headers: getSystemHeaders(),
				}),
			},
		),

		getUpriseCustomLensTypes: builder.query<
			ContactLensesOptionWithName[],
			void
		>({
			query: query => ({
				url: `${ServiceBaseUrl.ContactLenses}/uprise/custom-lens-types`,
				headers: getSystemHeaders(),
			}),
		}),

		getUpriseCustomLensTypeMaterials: builder.query<
			ContactLensesOptionWithName[],
			UpriseLensTypeId
		>({
			query: lensTypeId => ({
				url: `${ServiceBaseUrl.ContactLenses}/uprise/custom-lens-types/${lensTypeId}/materials`,
				headers: getSystemHeaders(),
			}),
		}),

		getById: builder.query<ContactLensesCatalogData, string>({
			query: contactLensesId => ({
				url: `${ServiceBaseUrl.ContactLenses}/${contactLensesId}`,
				headers: getSystemHeaders(),
			}),
		}),

		createContactLens: builder.mutation<
			void,
			ContactLensesBase & { legalEntityId?: string }
		>({
			query: ({ legalEntityId, ...body }) => ({
				url: `${ServiceBaseUrl.ContactLenses}`,
				method: 'POST',
				body: JSON.stringify({
					...body,
					...(legalEntityId && { legalEntityId }),
				}),
			}),
			invalidatesTags: [ServicesApiTag.ContactLenses],
		}),

		updateContactLens: builder.mutation<
			void,
			ContactLensesCatalogData & { legalEntityId?: string }
		>({
			query: ({ legalEntityId, ...body }) => ({
				url: `${ServiceBaseUrl.ContactLenses}/${body._id}${
					legalEntityId ? `?legalEntityId=${legalEntityId}` : ''
				}`,
				method: 'PUT',
				body: {
					...body,
					additionalParams: body.additionalParams
						? JSON.stringify(body.additionalParams)
						: '',
				},
			}),
			invalidatesTags: [ServicesApiTag.ContactLenses],
		}),

		enableContactLens: builder.mutation<
			void,
			{
				id: string
				value: { enabled: boolean; additionalParams?: any }
				legalEntityId?: string
			}
		>({
			query: ({ id, value, legalEntityId }) => ({
				url: `${ServiceBaseUrl.ContactLenses}/${id}${
					legalEntityId ? `?legalEntityId=${legalEntityId}` : ''
				}`,
				method: 'PATCH',
				body: {
					path: `enabled`,
					value: value.enabled,
					additionalParams: value.additionalParams
						? JSON.stringify(value.additionalParams)
						: '',
				},
			}),
			invalidatesTags: [ServicesApiTag.ContactLenses],
		}),

		exportLECatalog: builder.query<
			string,
			{
				legalEntityId?: string
			}
		>({
			query: ({ legalEntityId }) => ({
				url: `${ServiceBaseUrl.ContactLenses}/export${
					legalEntityId ? `?legalEntityId=${legalEntityId}` : ''
				}`,
				responseHandler: async (res: Response): Promise<string> =>
					window.URL.createObjectURL(await res.blob()),
			}),
		}),

		checkContactLensExists: builder.query<
			{ productExists: boolean },
			{ name: string; brand: string; lensType: string; legalEntityId?: string }
		>({
			query: params => ({
				url: `${ServiceBaseUrl.ContactLenses}/exists?${qs.stringify(params)}`,
				headers: getSystemHeaders(),
			}),
		}),

		importLECatalog: builder.mutation<
			{
				status: 'SUCCESS' | 'PARTIAL_SUCCESS' | 'ERROR' | 'PARTIAL_SUCCESS'
				totalRows: number
				successCount: number
				failureCount: number
				errors: { row: number; code: string; message: string; field?: string }[]
			},
			{
				file: string
				fileName: string
				legalEntityId?: string
			}
		>({
			query: ({ file, fileName, legalEntityId }) => ({
				url: `${ServiceBaseUrl.ContactLenses}/import`,
				method: 'POST',
				body: JSON.stringify({
					file,
					fileName,
					...(legalEntityId && { legalEntityId }),
				}),
			}),
			invalidatesTags: [ServicesApiTag.ContactLenses],
			extraOptions: { maxRetries: 0 },
		}),
	}),
})

const useGetDebounced = (query: string, legalEntityId?: string, limit?: number) => {
	const debouncedQuery = useDebounce(query, 500)

	return contactLensesApi.useGetQuery(
		{ query: debouncedQuery, legalEntityId, limit },
		{
			refetchOnMountOrArgChange: true,
		},
	)
}

export const useGetOptionsQuery = (
	params: ContactLensesOptionsParams,
	skip?: boolean,
) => {
	return contactLensesApi.useGetOptionsQuery(params, { skip })
}

export const useGetUpriseCustomLensTypes = (skip?: boolean) => {
	return contactLensesApi.useGetUpriseCustomLensTypesQuery(undefined, { skip })
}

export const useGetUpriseCustomLensTypeMaterials = (
	lensTypeId?: UpriseLensTypeId,
	skip?: boolean,
) => {
	return contactLensesApi.useGetUpriseCustomLensTypeMaterialsQuery(
		lensTypeId || '',
		{
			skip: skip || !lensTypeId,
		},
	)
}

export const useLazyGetOptionsQuery = () => {
	return contactLensesApi.useLazyGetOptionsQuery()
}

export const useContactLensesApi = {
	getDebounced: useGetDebounced,
}

export const {
	useCreateContactLensMutation,
	useUpdateContactLensMutation,
	useEnableContactLensMutation,
	useImportLECatalogMutation,
	useExportLECatalogQuery,
	useLazyExportLECatalogQuery,
	useLazyCheckContactLensExistsQuery,
} = contactLensesApi
